<template>
    <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
        <div class="flex min-h-screen items-center justify-center p-4">
            <!-- Background overlay -->
            <div class="fixed inset-0 bg-black bg-opacity-75 transition-opacity" aria-hidden="true" @click="close"></div>

            <!-- Modal panel -->
            <div class="relative w-full max-w-lg transform overflow-hidden rounded-lg bg-neutral-900 p-6 text-left align-middle shadow-xl transition-all">
                <!-- Close button -->
                <button
                    @click="close"
                    class="absolute top-4 right-4 text-neutral-400 hover:text-white transition-colors"
                    aria-label="Close"
                >
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                <div class="mt-2">
                    <h3 class="text-2xl font-bold text-white mb-6" id="modal-title">
                        {{ title }}
                    </h3>

                    <form @submit.prevent="submitFeedback" class="space-y-6">
                        <!-- Feedback Type -->
                        <div>
                            <label class="block text-sm font-medium text-neutral-300 mb-2">
                                What kind of feedback is this?
                            </label>
                            <select
                                v-model="form.type"
                                class="w-full px-4 py-2 text-white bg-neutral-800 border border-neutral-700 rounded-lg focus:border-brand-primary-500 focus:ring-1 focus:ring-brand-primary-500"
                            >
                                <option value="feedback">General Feedback</option>
                                <option value="testimonial">Testimonial</option>
                                <option value="bug">Bug Report</option>
                                <option value="feature">Feature Request</option>
                            </select>
                        </div>

                        <!-- Name -->
                        <div>
                            <label class="block text-sm font-medium text-neutral-300 mb-2">
                                What should we call you? (Optional)
                            </label>
                            <input
                                type="text"
                                v-model="form.name"
                                class="w-full px-4 py-2 text-white bg-neutral-800 border border-neutral-700 rounded-lg focus:border-brand-primary-500 focus:ring-1 focus:ring-brand-primary-500"
                                placeholder="Your name"
                            >
                        </div>

                        <!-- Email -->
                        <div>
                            <label class="block text-sm font-medium text-neutral-300 mb-2">
                                Your email (Optional, but helpful if we need to follow up)
                            </label>
                            <input
                                type="email"
                                v-model="form.email"
                                class="w-full px-4 py-2 text-white bg-neutral-800 border border-neutral-700 rounded-lg focus:border-brand-primary-500 focus:ring-1 focus:ring-brand-primary-500"
                                placeholder="your@email.com"
                            >
                        </div>

                        <!-- Rating (shown for testimonials) -->
                        <div v-if="form.type === 'testimonial'">
                            <label class="block text-sm font-medium text-neutral-300 mb-2">
                                How would you rate your experience?
                            </label>
                            <div class="flex space-x-2">
                                <button
                                    v-for="star in 5"
                                    :key="star"
                                    type="button"
                                    @click="form.rating = star"
                                    class="text-2xl focus:outline-none transition-colors"
                                    :class="star <= form.rating ? 'text-yellow-400' : 'text-neutral-400 hover:text-yellow-400'"
                                >
                                    ★
                                </button>
                            </div>
                        </div>

                        <!-- Title (shown for testimonials and feature requests) -->
                        <div v-if="['testimonial', 'feature'].includes(form.type)">
                            <label class="block text-sm font-medium text-neutral-300 mb-2">
                                {{ form.type === 'testimonial' ? 'Title your testimonial' : 'What feature would you like?' }}
                            </label>
                            <input
                                type="text"
                                v-model="form.title"
                                class="w-full px-4 py-2 text-white bg-neutral-800 border border-neutral-700 rounded-lg focus:border-brand-primary-500 focus:ring-1 focus:ring-brand-primary-500"
                                :placeholder="form.type === 'testimonial' ? 'e.g., &quot;Game-changing experience!&quot;' : 'e.g., &quot;Dark mode support&quot;'"
                            >
                        </div>

                        <!-- Content -->
                        <div>
                            <label class="block text-sm font-medium text-neutral-300 mb-2">
                                {{ contentLabel }}
                            </label>
                            <textarea
                                v-model="form.content"
                                rows="4"
                                class="w-full px-4 py-2 text-white bg-neutral-800 border border-neutral-700 rounded-lg focus:border-brand-primary-500 focus:ring-1 focus:ring-brand-primary-500"
                                :placeholder="contentPlaceholder"
                                required
                            ></textarea>
                        </div>

                        <!-- Submit Button -->
                        <div class="mt-6">
                            <button
                                type="submit"
                                class="w-full px-6 py-3 text-white font-bold bg-brand-primary-500 hover:bg-brand-primary-600 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                :disabled="submitting"
                            >
                                <svg v-if="submitting" class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                {{ submitting ? 'Sending...' : 'Send Feedback' }}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'Feedback',
    data() {
        return {
            isOpen: false,
            form: {
                type: 'feedback',
                content: '',
                name: '',
                email: '',
                title: '',
                rating: null,
                source: ''
            },
            submitting: false,
            success: false,
            error: null
        };
    },
    computed: {
        title() {
            const titles = {
                feedback: 'Share Your Thoughts',
                testimonial: 'Tell Us Your Story',
                bug: 'Found a Bug?',
                feature: 'Got an Idea?'
            };
            return titles[this.form.type] || 'Share Your Feedback';
        },
        contentLabel() {
            const labels = {
                feedback: 'What\'s on your mind?',
                testimonial: 'Share your experience',
                bug: 'What happened?',
                feature: 'Tell us more about your idea'
            };
            return labels[this.form.type] || 'Your feedback';
        },
        contentPlaceholder() {
            const placeholders = {
                feedback: 'We\'d love to hear what you think...',
                testimonial: 'How has this helped you? What did you love?',
                bug: 'What were you doing when the bug occurred?',
                feature: 'How would this feature help you?'
            };
            return placeholders[this.form.type] || 'Share your thoughts...';
        }
    },
    methods: {
        close() {
            this.isOpen = false;
        },
        submitFeedback() {

            if (this.submitting) {
                return false;
            }

            this.submitting = true;

            return axios.post(`${window.app.env.apiUrl}/reviews`, this.form)
                .then(() => {
                    window.app.snackbar({
                        message: 'Thank you for your feedback!',
                        type: 'success',
                        duration: 5000,
                    });
                    this.resetForm();
                    this.close();
                })
                .catch(error => {
                    console.error('Error submitting feedback:', error);
                    window.app.snackbar({
                        message: error.response?.data?.message || 'Failed to submit feedback. Please try again.',
                        type: 'error',
                        duration: 5000,
                    });
                })
                .finally(() => {
                    this.submitting = false;
                });
        },
        resetForm() {
            this.form = {
                type: 'feedback',
                content: '',
                name: '',
                email: '',
                title: '',
                rating: null,
                source: ''
            };
        }
    },
    mounted() {
        window.app.on('openFeedbackModal', () => {
            this.isOpen = true;
        });
    },
    beforeUnmount() {
        window.app.off('openFeedbackModal');
    }
};
</script>
